import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";

import { ThemeProperties } from "../../../Theme/ThemeProperties";

import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import TextWithCopy from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/TextWithCopy";
import CustomTag from "../../../Components/CustomTag";
import AssetDetailView from "./AssetDetailView";
import CreateEditAssetDialog from "./CreateEditAssetDialog";
import useAssetConfig from "./hooks/useAssetConfig";
import useAssets from "./hooks/useAssets";
import {
    assetCustodyLinkTarget,
    assetSelectionRow,
    pendingWorkOrderNote,
    buildAssetCSVColumnsPayload,
    buildAssetCSVRows,
    buildWorkOrderPrefillState,
    formatAssetCell,
    getAssetDefaultSelectedColumns,
    getAssetRowActions,
    getBadgeTheme,
    isActionColumn,
    mapAssetFiltersToParams,
    prepareSelectAllWorkOrder,
    selectAllBlocksWorkOrder,
    selectionBlocksWorkOrder,
    withSelectionCleared,
    withUnassignedStateOption,
} from "./assetTableHelpers";
import useSelectAllAcrossPages from "../hooks/useSelectAllAcrossPages";
import { MAX_ITEMS_PER_PASTE } from "../WorkOrders/workOrderHelpers";
import { PrimaryFormActionButton } from "../../../Features/Table/GenericDraggableTable/TableStyles";
import useConfiguredFilters from "../hooks/useConfiguredFilters";
import { assetListAction, getAssets, getAssetSearchSuggestions } from "../../../Api/AssetApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import { getAvailableEmailDomains } from "../../../Helper/Organization/EmailDomains";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { STATUS_PALETTE } from "../statusPalette";
import { ASSET_MANAGEMENT_LABEL } from "../../../Static/ModuleLabels";

// The Assets tab: the org's serial register.
//
// NOTHING HERE MOVES AN ASSET. currentStateCode, currentEntityID and
// receiptCount are a CACHE of assetLifecycleRecord -- the backend's request
// schemas refuse them on create and update -- so they are read here and never
// written. State and custody change only when a checker approves a work order,
// which lives in the Work Orders tab.
//
// NOTHING HERE REGISTERS ONE EITHER. An asset now enters the system through a
// Vendor Intake work order and only through one: registering a serial from this
// table wrote a row with no state, no custody and no movement behind it, which
// is a fact about an asset that nobody can explain afterwards. The "+ Register
// Asset" button is gone and so is the create path behind it.
//
// WHAT THIS TABLE IS FOR NOW: reading the register, and STARTING a work order
// from it. Tick the serials, press "+ New Work Order", and they arrive in the
// form's serials box -- which is the direction the work actually runs, rather
// than typing serials into a form that were already on screen a moment ago.
//
// Columns, search fields and their order are config (metaInfo.table.
// assetsTable); the lifecycle states behind the state column and the State
// filter are a TABLE, read through GET /api/assets/config, because five
// foreign keys point at assetState.code and you cannot foreign-key to a JSON
// blob.

// Module level so the default has a STABLE identity. An inline arrow would be
// a new function every render, and the effects below take it as a dependency.
const NO_SELECTION_REPORT = () => {};

// The marker itself, so both branches of the state cell draw the same thing.
//
// BESIDE the state, never instead of it. An asset held by a warehouse with a
// dispatch awaiting approval is still, right now, where the state says it is.
// Which column the pending marker belongs beside. The state column's
// dataIndex, not its rendering type -- see the note at the call site.
const STATE_COLUMN = "currentStateLabel";

const PendingWorkOrderBadge = ({ record, pending }) => (
    // ONE mechanism, not two. describeChild makes the tooltip an
    // aria-describedby on this child, so an aria-label here as well means the
    // same sentence is announced twice. role="status" is what makes the
    // element announceable at all -- aria-label on a bare span is ignored by
    // several screen readers.
    <PrimaryTooltip title={pending.tooltip} describeChild>
        <Box
            component="span"
            role="status"
            data-testid={`asset-pending-wo-${record?.id}`}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "18px",
                height: "18px",
                px: 0.5,
                borderRadius: "9px",
                fontSize: "11px",
                fontWeight: 600,
                lineHeight: 1,
                cursor: "default",
                // The module's own amber: something needs attention but
                // nothing is wrong.
                color: STATUS_PALETTE.warning.fg,
                backgroundColor: STATUS_PALETTE.warning.bg,
                border: `1px solid ${STATUS_PALETTE.warning.border}`,
            }}
        >
            {pending.count}
        </Box>
    </PrimaryTooltip>
);

const AssetsTabContent = ({
    metaInfo,
    canEdit = false,
    // Passed down rather than recomputed. The module shell already resolved it
    // for its floating button, from a config fetch this tab would otherwise
    // repeat on every visit -- and two copies of a permission predicate drift.
    canRaiseWorkOrder = false,
    // Where the user is standing, so the form they are about to open can send
    // them back to it. The LABEL comes from tabsConfig and is per-org, which
    // is why it travels rather than being looked up at the other end.
    tabSlug = "",
    tabLabel = "",
    // Reports how many rows are ticked, so the shell's floating button can
    // stand down while the table header is drawing its own. Defaulted to a
    // no-op so this tab still renders anywhere it is mounted without one.
    onSelectionCountChange = NO_SELECTION_REPORT,
    // --- reuse by the entity page ------------------------------------------
    // Narrowing the PAGE imposes and the user cannot clear -- see useAssets.
    // The Assets tab passes nothing and gets every asset, as before.
    pinnedFilters = null,
    // What this list is, when it is not the whole tab. The entity page says
    // "Assets with this customer"; the heading is the only thing that tells a
    // reader the table is already narrowed.
    title = "Assets",
    // Named per caller because the reason a list is empty differs: the tab is
    // empty when an org has no assets, the entity page when nothing is with
    // that entity yet.
    emptyHint = "",
}) => {
    const navigate = useNavigate();
    const {
        columnsConfig,
        columnOrdering,
        searchableFields,
        isConfigured,
    } = useAssetConfig(metaInfo);

    const {
        records,
        total,
        isLoading,
        errorMessage,
        page,
        limit,
        setPage,
        applySearch,
        applySort,
        applyFilterParams,
        requestArgs,
        refetch,
    } = useAssets({ pinnedFilters });

    const { openNotification } = useToast();

    // globalConfig is copied onto every page's metaInfo by the backend, so the
    // domain list is already in hand -- adding one for an org is a DB change.
    const emailDomains = useMemo(() => getAvailableEmailDomains(metaInfo), [metaInfo]);

    // Edit only. The dialog has no create path any more -- assets are born in a
    // work order -- and the one way in is the Edit button on the detail view,
    // where the asset being changed is on screen beside the form.
    const [dialogAsset, setDialogAsset] = useState(null);
    const [viewTarget, setViewTarget] = useState(null);

    const openEdit = useCallback((record) => {
        setViewTarget(null);
        setDialogAsset(record);
    }, []);

    const closeEdit = useCallback(() => setDialogAsset(null), []);

    // --- row selection ----------------------------------------------------
    // Selection drives the batch actions in the table header: with rows ticked
    // the download and the mail cover exactly those rows, with the header
    // ticked (select-all mode) every row matching the filters, and with none
    // ticked the whole filtered set (alwaysEnableActions), so the controls are
    // never a dead button waiting on a tick. The narrowing is the SERVER's --
    // see selectionRequestFields -- the same contract the Inventory lists use.
    //
    // {id, serialNumber, custody} and not bare ids. Selection survives paging
    // and `records` only ever holds the page on screen, so resolving an id
    // back to a serial at click time would silently drop everything ticked on
    // a page the user has since left.
    const {
        isSelectAll,
        selectedRows,
        count: selectionCount,
        requestFields: selectionFields,
        capError: selectionCapError,
        clearSelection,
        rowSelection,
        getTotalRowSelection,
        getTotalRowSelectionCount,
    } = useSelectAllAcrossPages({ records, total, toRow: assetSelectionRow });

    // The module shell hides its floating "+ New Work Order" while rows are
    // ticked, because the table header draws its own and the two mean
    // different things. Reported as a COUNT rather than as the rows: the shell
    // has no business with the selection itself, and handing it the array
    // would invite a second feature to read it from up there.
    useEffect(() => {
        onSelectionCountChange(selectionCount);
    }, [selectionCount, onSelectionCountChange]);


    // Leaving the tab with rows ticked would otherwise leave the shell
    // believing a selection exists on a tab that is no longer mounted, and the
    // floating button would stay hidden on Network and Work Orders.
    useEffect(
        () => () => onSelectionCountChange(0),
        [onSelectionCountChange]
    );

    // Carries the ticked serials AND the tab to come back to in ROUTER STATE,
    // not the query string: a selection can be two hundred serials. The form
    // works without any of it, so the blank case needs no branch here.
    //
    // SELECT-ALL MODE FETCHES. The client holds one page, not the selection,
    // so every asset matching the table's own requestArgs is read through
    // getAssets and checked like a hand-picked selection -- see
    // prepareSelectAllWorkOrder. The work order limit is checked before the
    // fetch and again against the server's total.
    const [isPreparingWorkOrder, setIsPreparingWorkOrder] = useState(false);

    // A select-all fetch belongs to the selection it started from. Its token is
    // bumped when that selection or its query changes (Clear, header untick,
    // filter, search, sort) and on unmount (tab switch), so a late response
    // never opens the form with rows the user no longer has selected. The ref
    // also guards double clicks, which a state flag read in a closure cannot.
    const selectionTokenRef = useRef(0);
    const workOrderInFlightRef = useRef(false);
    // By value: useAssets rebuilds requestArgs on a page change too, and
    // paging keeps the selection, so it must not cancel the fetch.
    const queryKey = JSON.stringify(requestArgs || {});
    useEffect(() => {
        selectionTokenRef.current += 1;
    }, [isSelectAll, queryKey]);
    useEffect(() => () => {
        selectionTokenRef.current += 1;
    }, []);

    const openWorkOrderForSelection = useCallback(async () => {
        const origin = { originTab: tabSlug, originLabel: tabLabel };
        if (!isSelectAll) {
            navigate("/asset-management/work-orders/new", {
                state: buildWorkOrderPrefillState(selectedRows, origin),
            });
            return;
        }
        if (workOrderInFlightRef.current) return;
        workOrderInFlightRef.current = true;
        const token = selectionTokenRef.current;
        const isCurrent = () => selectionTokenRef.current === token;
        setIsPreparingWorkOrder(true);
        try {
            const result = await prepareSelectAllWorkOrder({
                fetchPage: getAssets,
                requestArgs,
                total,
                maxRows: MAX_ITEMS_PER_PASTE,
                successCode: CODE.SUCCESS,
                ...origin,
            });
            // The selection changed (or the tab unmounted) while this ran.
            if (!isCurrent()) return;
            if (!result.ok) {
                openNotification({ text: result.reason, icon: "toast-error" });
                return;
            }
            navigate("/asset-management/work-orders/new", { state: result.state });
        } catch (error) {
            if (!isCurrent()) return;
            openNotification({
                text: "Could not load the selected assets",
                icon: "toast-error",
            });
        } finally {
            workOrderInFlightRef.current = false;
            setIsPreparingWorkOrder(false);
        }
    }, [
        navigate,
        selectedRows,
        tabSlug,
        tabLabel,
        isSelectAll,
        requestArgs,
        total,
        openNotification,
    ]);

    // Empty while the create flow is being finished -- see getAssetRowActions.
    // The Actions column is dropped rather than rendered over blank cells.
    const rowActions = useMemo(() => getAssetRowActions(), []);

    // Cell renderers for the column types this table's config uses.
    //
    // customRenderers is checked FIRST by TableColumnValueRenderer, so these
    // are an extension point rather than a fork -- `status-chip`, `date`,
    // `date-time` and `actions` have no case in the shared switch and would
    // otherwise render as an empty cell.
    //
    // Signature is POSITIONAL: TableColumnValueRenderer calls
    // customRenderers[type](record, config). Destructuring `record` off the
    // record object itself yields undefined, which on the Network tab silently
    // turned Edit into a CREATE form.
    //
    // Every one of these takes its display text from formatAssetCellForCSV --
    // the same function buildAssetCSVRows calls -- so the file cannot drift
    // from the screen. That is the repo's Data Transform Parity Rule held by
    // construction rather than by remembering.
    const customRenderers = useMemo(
        () => ({
            // `text` is overridden, not left to the shared switch, for one
            // reason: that branch renders `value || defaultValue`, so a
            // NUMERIC ZERO draws as "-" on screen while the CSV carries 0.
            // Routing it through the same formatter as every other column
            // fixes the falsy test and the parity break together.
            //
            // The column that first showed this was receiptCount, now retired.
            // The override stays, because the next count column -- a pending
            // work order count, say -- would hit it again.
            text: (record, config) => (
                <Typography variant="fw400s14lh24c3D4B5A">
                    {formatAssetCell(config, record)}
                </Typography>
            ),
            "date": (record, config) => (
                <Typography variant="fw400s14lh24c3D4B5A">
                    {formatAssetCell(config, record)}
                </Typography>
            ),
            "date-time": (record, config) => (
                <Typography variant="fw400s14lh24c3D4B5A">
                    {formatAssetCell(config, record)}
                </Typography>
            ),
            "status-chip": (record, config) => {
                const label = formatAssetCell(config, record);
                const raw = record?.[config?.dataIndex];
                // SCOPED TO THE STATE COLUMN, not to every status-chip. This
                // renderer is keyed on a rendering TYPE, and an org that
                // configures a second chip column would otherwise get the same
                // pending count on the row twice, beside a value that has
                // nothing to do with work orders.
                //
                // COMPUTED BEFORE the no-state return: an asset awaiting
                // approval on its first intake has no state at all, and that
                // is the case the marker is most useful for.
                const pending = config?.dataIndex === STATE_COLUMN
                    ? pendingWorkOrderNote(record)
                    : null;
                // An asset registered outside a work order has no state at
                // all. A chip around a dash would read as a state called "-".
                if (raw === null || raw === undefined || raw === "") {
                    return (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                            <Typography variant="fw400s14lh24c3D4B5A">{label}</Typography>
                            {pending && <PendingWorkOrderBadge record={record} pending={pending} />}
                        </Box>
                    );
                }
                const theme = getBadgeTheme(
                    record?.[config?.badgeThemeDataIndex || "currentStateBadgeColorTheme"]
                );
                return (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                        <CustomTag
                            text={label}
                            backgroundColor={theme.bg}
                            fontColor={theme.fg}
                            fontSize="12px"
                            fontWeight="500"
                        />
                        {pending && <PendingWorkOrderBadge record={record} pending={pending} />}
                    </Box>
                );
            },
            // Current Custody is a LINK to whoever is holding the asset.
            //
            // The name alone was a dead end: the obvious next question after
            // "who has BAT-0002" is "what else do they have", and answering it
            // meant leaving for the Network tab, picking the right sub-tab and
            // finding the row. The entity view already answers it, and since
            // it has a route both this cell and the Network row can reach the
            // same page.
            //
            // The ID comes from the column config rather than a hardcoded
            // field, so the same renderer serves any future column that names
            // an entity -- a destination, a previous holder -- by pointing
            // `entityIDDataIndex` at that column's own id.
            "entity-link": (record, config) => {
                const label = formatAssetCell(config, record);
                // The two unlinkable cases -- nobody holding it, and a name
                // with no id behind it -- are decided in one helper so the
                // rule is testable and this stays a renderer.
                const href = assetCustodyLinkTarget(record, config);
                if (!href) {
                    return (
                        <Typography variant="fw400s14lh24c3D4B5A">{label}</Typography>
                    );
                }
                return (
                    <Typography
                        component="a"
                        href={href}
                        data-testid={`asset-custody-link-${record?.id}`}
                        // THE PRODUCT'S LINK VARIANT, not a colour chosen
                        // here. It carries #0061CA and -- crucially -- the
                        // `!important` that every text variant in BaseTheme
                        // carries. The first version of this used the ordinary
                        // body variant and set a colour through `sx`, which
                        // loses to `fw400s14lh24c3D4B5A`'s own
                        // `color: #3D4B5A !important`: the anchor rendered and
                        // navigated correctly, and looked like plain text.
                        //
                        // Picking a hex here was the error underneath that.
                        // #4D44D8 is the HOVER colour DeviceIDLink uses, not
                        // the resting one, so the cell was reaching for the
                        // wrong end of a state it had no business naming.
                        variant="fw400s14lh24c0061CA"
                        // A real anchor, so the row is keyboard reachable and
                        // middle-click opens it in a tab. The click is still
                        // intercepted for client-side navigation.
                        onClick={(event) => {
                            if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                            event.preventDefault();
                            navigate(href);
                        }}
                        sx={{
                            textDecoration: "none",
                            cursor: "pointer",
                            // Matches DeviceIDLink: blue at rest, purple on
                            // hover. !important because the variant's own
                            // colour carries it and would otherwise win here
                            // too.
                            // && DOUBLES THE CLASS SPECIFICITY. The variant's
                            // own colour is !important too, and two
                            // !important rules at equal specificity are
                            // resolved by emotion's injection order -- which
                            // is not something to rely on. Doubling decides it
                            // in the selector instead.
                            "&&:hover": {
                                color: `${ThemeProperties.purple} !important`,
                                textDecoration: "underline",
                            },
                        }}
                    >
                        {label}
                    </Typography>
                );
            },
            // The serial is the row's identity, so it doubles as the way in.
            // GenericDraggableTable exposes no onRow hook, and adding one
            // would mean editing a table shared by every list in the product.
            "text-with-copy": (record, config) => {
                const value = record?.[config?.dataIndex];
                if (value === null || value === undefined || value === "") {
                    return (
                        <Typography variant="fw400s14lh24c3D4B5A">
                            {formatAssetCell(config, record)}
                        </Typography>
                    );
                }
                return (
                    <Box
                        data-testid={`asset-open-${record?.id}`}
                        onClick={() => setViewTarget(record)}
                        sx={{
                            display: "inline-flex",
                            cursor: "pointer",
                            // The same hover the custody link has. Without it
                            // the two links sit side by side in one row and
                            // behave differently under the cursor.
                            "&&:hover .MuiTypography-root": {
                                color: `${ThemeProperties.purple} !important`,
                            },
                        }}
                    >
                        {/* TextWithCopy's copy button stops propagation, so
                            copying the serial does not also open the asset. */}
                        <TextWithCopy
                            text={value}
                            type={config?.copyToClipBoardTypeName}
                            // textVariant, NOT textStyle. TextWithCopy renders
                            // `style={textStyle}` on a Typography whose variant
                            // sets `color: #3D4B5A !important` -- and an inline
                            // style loses to !important, so the serial rendered
                            // as ordinary dark text while still being a link.
                            textVariant="fw400s14lh24c0061CA"
                        />
                    </Box>
                );
            },
        }),
        [navigate]
    );

    // Every column the picker may offer, actions included. `allColumns` feeds
    // the picker and `columns` the table, so the visible set is a subset of
    // this rather than a second build with its own rules.
    const allColumns = useMemo(() => {
        if (!columnOrdering.length) return [];
        return getColumnsConfig({
            // Passed straight through: getColumnsConfig already does the named
            // lookup by key.
            tableColumns: columnsConfig,
            tableColumnOrder: columnOrdering,
            shouldShowActionMenu: () => true,
            customRenderers,
        });
    }, [columnsConfig, columnOrdering, customRenderers]);

    // The actions column is never in the picker: it is not data, and letting a
    // user hide it would take away every row action with no way back.
    const pickableColumns = useMemo(
        () => allColumns.filter((column) => !isActionColumn(column)),
        [allColumns]
    );

    const defaultSelectedColumns = useMemo(
        () => getAssetDefaultSelectedColumns(columnsConfig, columnOrdering),
        [columnsConfig, columnOrdering]
    );

    const [selectedColumns, setSelectedColumns] = useState([]);

    useEffect(() => {
        setSelectedColumns(defaultSelectedColumns);
    }, [defaultSelectedColumns]);

    const columns = useMemo(() => {
        // Matched on dataIndex, because that is what TableColumns writes into
        // selectedColumns. Matching on the config key instead works only while
        // key and dataIndex happen to be the same string, and fails silently
        // and totally the moment a config makes them differ.
        const visible = columnOrdering
            .map((key) => pickableColumns.find((column) => column.key === key))
            .filter((column) => column && selectedColumns.includes(column.dataIndex));
        // NO ACTIONS COLUMN WHEN THERE ARE NO ACTIONS. With View and Edit gone
        // the column holds Delete alone, and a user without that grant would
        // otherwise get an "Actions" header over a column of empty cells --
        // which reads as a table whose buttons failed to load.
        const actions = rowActions.length ? allColumns.filter(isActionColumn) : [];
        return [...visible, ...actions];
    }, [columnOrdering, selectedColumns, pickableColumns, allColumns, rowActions]);

    // Header labels, in the order shown. GenericDraggableTable reorders the
    // CSV by this list, so anything missing from it becomes an empty column.
    //
    // `title` is only a fallback where it is still a string: getColumnsConfig
    // replaces it with a render function, and a function in this list becomes
    // an empty CSV column rather than an error.
    const headerLabelOf = useCallback(
        (column) =>
            typeof column.headerLabel === "string"
                ? column.headerLabel
                : typeof column.title === "string"
                ? column.title
                : column.dataIndex,
        []
    );

    const [draggedOrder, setDraggedOrder] = useState(null);

    // The table keeps drag order in its own state, so without this the CSV
    // stays in the pre-drag order while the screen shows the new one.
    const onColumnOrderChange = useCallback(
        (cols) =>
            setDraggedOrder(
                (cols || []).filter((c) => !isActionColumn(c)).map(headerLabelOf)
            ),
        [headerLabelOf]
    );

    // Dropped whenever the visible set changes: a drag order captured before a
    // column was hidden names a column the file no longer has.
    useEffect(() => {
        setDraggedOrder(null);
    }, [selectedColumns]);

    const columnOrder = useMemo(
        () =>
            draggedOrder ||
            columns.filter((c) => !isActionColumn(c)).map(headerLabelOf),
        [columns, draggedOrder, headerLabelOf]
    );

    // The column set the SERVER is told about: dataIndex + headerLabel pairs,
    // exactly what convertDataForCSV renames by.
    //
    // ONE payload for both actions, so the download and the mail are the
    // same file. See buildAssetCSVColumnsPayload.
    const exportColumns = useMemo(
        () => buildAssetCSVColumnsPayload(columns),
        [columns]
    );

    // --- narrowing the list -----------------------------------------------
    // Search, sort and the filter panel all narrow the result set, so every
    // one of them drops the row selection first -- see
    // withSelectionCleared. useMemo rather than useCallback because the
    // wrapper returns the handler.
    //
    // GenericDraggableTable.js calls onSortChange(columnKey, "asc"|"desc"),
    // already normalised. The key is the config key, which the backend's
    // SORTABLE_COLUMNS accepts as-is -- currentStateLabel included, sorted by
    // the label text the user is reading rather than the code behind it.
    const handleSort = useMemo(
        () =>
            withSelectionCleared(clearSelection, (key, order) =>
                applySort(key || null, order || null)
            ),
        [applySort, clearSelection]
    );

    const handleSearch = useMemo(
        () =>
            withSelectionCleared(clearSelection, (selected, column) =>
                // TableSearch hands back an ARRAY of chips; the list endpoint
                // takes one `search` string, and an array serialises to
                // search[0]=..&search[1]=.. which unknown=RAISE rejects.
                applySearch(
                    Array.isArray(selected)
                        ? selected.filter(Boolean).join(",")
                        : selected,
                    column
                )
            ),
        [applySearch, clearSelection]
    );

    // --- filter panel -----------------------------------------------------
    // Declarations come from pageMeta (metaInfo.filterConfigurations.
    // assetsTable.filters) and their options from the SHARED
    // fetchFiltersDataObject, whose output is already the shape
    // GenericDraggableTableView's `filters` prop takes. An org with no block
    // declared gets {}, and the table renders no panel at all.
    const { filters: declaredFilters } = useConfiguredFilters(metaInfo, "assetsTable");

    // The State filter gets the one option its own option list cannot hold --
    // see withUnassignedStateOption. Everything else is passed through as the
    // shared resolver returned it.
    const filters = useMemo(
        () => withUnassignedStateOption(declaredFilters),
        [declaredFilters]
    );

    // The panel's own selection. The REQUEST state is the hook's single query
    // object; this is what the panel draws its ticks from, and the two are
    // written in the same handler rather than synced by an effect.
    const [selectedFilters, setSelectedFilters] = useState({});

    // The panel's one apply path.
    //
    // Selection is dropped first, through the same wrapper search and sort use:
    // ticked rows are ids and the export scopes the FILE by them, so a tick
    // naming a row the new filter removed produces an empty CSV with nothing on
    // screen to explain it.
    //
    // What the panel shows and what the request carries are the same object:
    // the whole selection is stored and the whole selection is mapped, so a
    // tick can never be silently dropped on the way to the wire.
    const applyFilters = useMemo(
        () =>
            withSelectionCleared(clearSelection, (next) => {
                const selection = next || {};
                setSelectedFilters(selection);
                applyFilterParams(mapAssetFiltersToParams(selection));
            }),
        [applyFilterParams, clearSelection]
    );

    // --- export -----------------------------------------------------------
    // Requests the SAME search/sort/filter arguments the list is showing, so
    // the file can never cover a different set of rows than the screen.
    //
    // The server builds the file. It renames the columns to these header
    // labels and localises the dates (convertDataForCSV over
    // _serialiseExportRow), and it does so from one frame for both actions --
    // which is what makes the download and the mail attachment the same file
    // rather than two files that agree today. Nothing here reformats what
    // comes back: re-running the cell formatter over an already-localised
    // "04 Mar 2025 03:41 PM" would fail to parse it and blank the column.
    //
    // The selection travels as resourceIDs / selectAll and the SERVER scopes
    // the file -- buildAssetCSVRows only fills the column placeholders. See
    // its comment.
    const getCSVData = useCallback(async () => {
        // More hand-ticked ids than the server accepts: say so and send
        // nothing. Thrown so onDownloadCSV writes no (empty) file.
        if (selectionCapError) {
            openNotification({ text: selectionCapError, icon: "toast-error" });
            throw new Error(selectionCapError);
        }
        const res = await assetListAction({
            actionType: "download-csv",
            columns: exportColumns,
            columnOrder,
            ...requestArgs,
            // Last: a filter key from pageMeta must never replace the selection.
            ...selectionFields,
        });
        if (res?.responseStatus?.code !== CODE.SUCCESS) {
            openNotification({
                text: res?.responseStatus?.msg || "Could not prepare the download",
                icon: "toast-error",
            });
            return [];
        }
        if (res?.data?.truncated) {
            openNotification({
                text: `Only the first ${res.data.limit} rows were exported. Narrow the filters for the rest.`,
                icon: "toast-error",
            });
        }
        return buildAssetCSVRows(res?.data?.records || [], columns);
    }, [
        exportColumns,
        columnOrder,
        selectionFields,
        selectionCapError,
        requestArgs,
        columns,
        openNotification,
    ]);

    // The emailed attachment is the download, addressed and sent: same
    // endpoint, same query, same `columns`, same selection fields, so the
    // server builds it from the same frame -- as the Inventory lists do. It
    // takes no `rows` payload -- AssetListActionSchema has no such field and
    // uses unknown=RAISE -- which is why none is sent.
    const onSendEmail = useCallback(
        async ({ sendTo, sendCc, subject, body }) => {
            if (selectionCapError) {
                openNotification({ text: selectionCapError, icon: "toast-error" });
                throw new Error(selectionCapError);
            }
            const res = await assetListAction({
                actionType: "mail",
                columns: exportColumns,
                columnOrder,
                sendTo,
                sendCc,
                subject,
                body,
                fileName: "assets",
                ...requestArgs,
                // Last: a filter key from pageMeta must never replace the selection.
                ...selectionFields,
            });
            if (res?.responseStatus?.code !== CODE.SUCCESS) {
                const message = res?.responseStatus?.msg || "Could not send the email";
                // Toasted AND thrown. The throw keeps the composer open so the
                // user does not lose what they typed; the toast is what they
                // actually see, because the composer swallows the rejection
                // into console.error.
                openNotification({ text: message, icon: "toast-error" });
                throw new Error(message);
            }
        },
        [exportColumns, columnOrder, selectionFields, selectionCapError, requestArgs, openNotification]
    );

    const getCSVFileName = useCallback(() => "assets.csv", []);

    // The selection-driven control, in the table header where the selection
    // is. Rendered only WITH a selection: it acts on the ticked rows, so an
    // always-present version would be a button whose meaning changes silently
    // from "these" to "none".
    //
    // NO COUNT OF ITS OWN. TableHeaderAndActions already draws "N selected"
    // in purple immediately to the left of this slot, on the same condition --
    // a second one here would put the number on screen twice, and the two
    // could disagree the moment either changed.
    //
    // The module's floating "+ New Work Order" stays and starts blank. These
    // are two different intents -- "move these assets" and "raise a request"
    // -- not one button drawn twice.
    // THE RULE LIVES ON THE BUTTON, not on the checkbox.
    //
    // A work order moves assets from ONE place, and a selection spanning two
    // holders cannot become one. That used to refuse the tick, which made the
    // table argue about a checkbox -- ticking a row is also how somebody
    // compares, downloads or mails a set, and refusing it took away three
    // working features to protect one.
    //
    // Disabled with the reason on hover instead: the refusal sits on the
    // control it applies to, and says which places, because "mixed custody"
    // tells somebody they are wrong without telling them what to change.
    //
    // In select-all mode only the work order LIMIT can be judged before the
    // fetch; the one-source rule is applied to the fetched rows on click, and
    // a refusal arrives as a toast.
    const workOrderBlock = useMemo(
        () =>
            isSelectAll
                ? selectAllBlocksWorkOrder(total, MAX_ITEMS_PER_PASTE)
                : selectionBlocksWorkOrder(selectedRows),
        [isSelectAll, total, selectedRows]
    );

    const tableHeaderActions = useMemo(() => {
        if (!canRaiseWorkOrder || !selectionCount) return null;
        return (
            <Box sx={{ display: "flex", alignItems: "center", ml: 1.5, flexShrink: 0 }}>
                {/* A SPAN, because MUI cannot attach a tooltip to a disabled
                    button -- a disabled element fires no events, so the
                    explanation would be missing exactly when it is needed. */}
                <PrimaryTooltip title={workOrderBlock.reason} describeChild>
                    <Box component="span" sx={{ display: "inline-flex" }}>
                        <PrimaryFormActionButton
                            data-testid="asset-new-work-order"
                            onClick={openWorkOrderForSelection}
                            disabled={workOrderBlock.blocked || isPreparingWorkOrder}
                            aria-busy={isPreparingWorkOrder}
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            {isPreparingWorkOrder && (
                                <CircularProgress
                                    size={14}
                                    color="inherit"
                                    sx={{ mr: 1 }}
                                    data-testid="asset-new-work-order-loading"
                                />
                            )}
                            + New Work Order
                        </PrimaryFormActionButton>
                    </Box>
                </PrimaryTooltip>
            </Box>
        );
    }, [
        canRaiseWorkOrder,
        selectionCount,
        openWorkOrderForSelection,
        workOrderBlock,
        isPreparingWorkOrder,
    ]);

    // "Not configured" is a real state with a real cause, not an empty list.
    // AssetManagement.jsx gates this tab on pagesContent.mounted, so reaching
    // here with no columns really does mean the table is unconfigured.
    if (!isConfigured) {
        return (
            <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ fontSize: 14, color: ThemeProperties.greyTextColor }}>
                    The assets table is not configured for this organisation.
                </Typography>
                <Typography
                    sx={{ fontSize: 13, color: ThemeProperties.greyTextColor, mt: 0.5 }}
                >
                    It is defined in page settings under
                    AssetManagement &rarr; table &rarr; assetsTable.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    "& .ant-table-column-sorter": { display: "none" },
                    "& .ant-table-column-has-sorters .ant-table-cell-content": {
                        cursor: "pointer",
                    },
                }}
            >
                <GenericDraggableTableView
                    title={title}
                    count={total}
                    // EXPLICITLY FALSE. TableHeaderAndActions defaults it to
                    // true, so dropping the label alone would leave an empty
                    // outlined button sitting beside the count.
                    showPrimaryButton={false}
                    customRightActions={Boolean(tableHeaderActions)}
                    customRightActionsView={tableHeaderActions}
                    tableColumns={columns}
                    tableData={records}
                    isLoading={isLoading}
                    pageNumber={page}
                    rowsPerPage={limit}
                    total={total}
                    onPageChange={setPage}
                    applySort={handleSort}
                    applySearch={handleSearch}
                    filters={filters}
                    selectedFilters={selectedFilters}
                    applyFilters={applyFilters}
                    searchableFields={searchableFields}
                    // The shared default fires the digital-twin device
                    // endpoint, which would offer battery IDs while the user
                    // types a serial. Argument order is not the same as the
                    // API's: DeviceSearchInput calls
                    // handler(term, columnName, limit).
                    searchSuggestionsHandler={(term, columnName, suggestionLimit) =>
                        getAssetSearchSuggestions(columnName, term, suggestionLimit)
                    }
                    disableSearch={!searchableFields.length}
                    initialColumns={pickableColumns}
                    selectedColumns={selectedColumns}
                    applySelectedColumns={setSelectedColumns}
                    defaultSelectedColumns={defaultSelectedColumns}
                    maxColumnsAllowed={pickableColumns.length}
                    columnOrder={columnOrder}
                    onColumnOrderChange={onColumnOrderChange}
                    getCSVData={getCSVData}
                    getCSVFileName={getCSVFileName}
                    onSendEmail={onSendEmail}
                    availableEmailDomains={emailDomains}
                    emailInfo={{
                        subject: "Assets export",
                        // Explicit: EmailComposerDialog only copies its default
                        // body into the payload as a side effect of the user
                        // editing the subject, so an unedited send arrived with
                        // an empty body.
                        body: `Attached is the asset list exported from ${ASSET_MANAGEMENT_LABEL}.`,
                    }}
                    // Batch actions stay live with nothing ticked: the natural
                    // default here is "everything I am looking at", and a
                    // permanently greyed Download would read as broken.
                    alwaysEnableActions={true}
                    // Select-all across pages: the header puts the table in
                    // select-all mode (every page ticked, count = filtered
                    // total); unticking one row leaves it and keeps the rest
                    // of the page. See ../selectionHelpers.js.
                    rowSelection={rowSelection}
                    getTotalRowSelection={getTotalRowSelection}
                    getTotalRowSelectionCount={getTotalRowSelectionCount}
                    setSelectedRowKeys={clearSelection}
                    onClearSelection={clearSelection}
                    disablePagination={false}
                    emptyMessage={
                        errorMessage ||
                        (columns.length
                            ? emptyHint || "No assets found"
                            : "Columns are not configured for assetsTable")
                    }
                />
            </Box>

            {/* Edit only, and mounted only with an asset in hand: the dialog
                no longer has a create mode to fall back to when `asset` is
                null. */}
            <CreateEditAssetDialog
                open={Boolean(dialogAsset)}
                asset={dialogAsset}
                metaInfo={metaInfo}
                onClose={closeEdit}
                onSuccess={() => {
                    closeEdit();
                    refetch();
                }}
            />

            <AssetDetailView
                open={Boolean(viewTarget)}
                asset={viewTarget}
                onClose={() => setViewTarget(null)}
                onEdit={canEdit ? openEdit : undefined}
            />
        </>
    );
};

export default AssetsTabContent;
